/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type EventStatus from '@linc-technologies/linced-common/enums/bookings/event-status';
import type Allocation from '@linc-technologies/linced-common/models/bookings/allocation';
import type Booking from '@linc-technologies/linced-common/models/bookings/booking';
import type BookingsDate from '@linc-technologies/linced-common/models/bookings/date';
import type Slot from '@linc-technologies/linced-common/models/bookings/slot';
import type TeacherGroupMapping from '@linc-technologies/linced-common/models/bookings/teacher-group-mapping';
import type School from '@linc-technologies/linced-common/models/school';
import {
  mapBy,
  resolved,
  uniq,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

// Proto: bookings.Event.
export default class Event extends Model {
  declare [Type]: 'bookings/event';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // name is the name of the event.
  @attr('string', { defaultValue: '' })
  declare name: string;

  // dates contains one or more dates covered by the event.
  @fragmentArray('bookings/date')
  declare dates: FragmentArray<BookingsDate>;

  // slotLength is the length of the booking slot (in minutes).
  @attr('number', { defaultValue: 0 })
  declare slotLength: number;

  // slotGap is the length of gap between booking slots (in minutes).
  @attr('number', { defaultValue: 0 })
  declare slotGap: number;

  // teacherGroupMappings contains one or more teacher group covered by the event.
  @fragmentArray('bookings/teacher-group-mapping')
  declare teacherGroupMappings: FragmentArray<TeacherGroupMapping>;

  // consecutiveBookings is a flag indicating whether caregivers can book consecutive
  // slots (with a configurable message) so that I can ensure caregivers have time to move between meetings
  @attr('boolean', { defaultValue: false })
  declare consecutiveBookings: boolean;

  // consecutiveBookingMessage is the message displayed to parents if they attempt to book consecutive
  // slots and consecutiveBookings is turned off.
  @attr('string', { defaultValue: '' })
  declare consecutiveBookingMessage: string;

  // status contains current event status.
  @attr('number', { defaultValue: 0 })
  declare status: EnumValue<typeof EventStatus>;

  // staffBookingManagement indicates if staff can manage their own bookings.
  @attr('boolean', { defaultValue: false })
  declare staffBookingManagement: boolean;

  // statusChangeReason can optionally include a string describing why the
  // change happened when events change status.
  @attr('string', { defaultValue: '' })
  declare statusChangeReason: string;

  // schoolId is the school the booking relates to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @alias('school.id')
  declare school_id: string | null | undefined;

  // campusId is the school campus the booking relates to.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  @hasMany('bookings/allocation', { async: true, inverse: 'eventId' })
  declare allocations: AsyncHasMany<Allocation>;

  @hasMany('bookings/booking', { async: true, inverse: 'eventId' })
  declare bookings: AsyncHasMany<Booking>;

  @hasMany('bookings/slot', { async: true, inverse: 'eventId' })
  declare slots: AsyncHasMany<Slot>;

  @computed('slotLength', 'slotGap')
  get slotFootprint() {
    return (
      // These attrs are numbers. parseInt converts them to strings first.
      Math.max(parseInt(this.slotLength as unknown as string), 1) +
      Math.max(parseInt(this.slotGap as unknown as string), 0)
    );
  }

  @computed('dates.@each.date')
  get dateRange() {
    if (this.dates.length === 1) {
      return moment(this.dates.firstObject!.date).format('Do MMMM YYYY');
    }
    return `${moment(this.dates.firstObject!.date).format(
      'Do MMMM YYYY',
    )} - ${moment(this.dates.lastObject!.date).format('Do MMMM YYYY')}`;
  }

  @computed('dates.[]')
  get totalDates() {
    return this.dates.length;
  }

  @computed('dates.@each.sessionCount')
  get totalSessions() {
    return this.dates.reduce((count, date) => {
      count += date.sessionCount;
      return count;
    }, 0);
  }

  @computed('dates.@each.flattenedSessions', 'slotFootprint', 'slotLength')
  get totalSlots() {
    return this.dates.reduce((count, date) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- the read was in the JS before the conversion.
      const anything = date.flattenedSessions;
      return (
        count +
        date.sessions.reduce((innerCount, session) => {
          // Session times have no default. A saved session has both.
          const startTime = moment
            .unix(session.startTime as number)
            .add(this.slotLength, 'minutes');
          const endTime = moment.unix(session.endTime as number);
          return (
            innerCount +
            Math.floor(
              endTime.diff(startTime, 'minutes') / this.slotFootprint,
            ) +
            1
          );
        }, 0)
      );
    }, 0);
  }

  @computed('totalSlots', 'teacherGroupMappings.@each.maxBookings')
  get totalBookings() {
    return (
      this.totalSlots *
      this.teacherGroupMappings.reduce((count, mapping) => {
        // maxBookings is a number. parseInt converts it to a string first.
        return count + parseInt(mapping.maxBookings as unknown as string);
      }, 0)
    );
  }

  @computed('bookings.[]', 'totalBookings')
  get availablePercent() {
    return Math.round(
      ((this.totalBookings - this.bookings.length) / this.totalBookings) * 100,
    );
  }

  @computed('bookings.[]', 'totalBookings')
  get bookedPercent() {
    return Math.round((this.bookings.length / this.totalBookings) * 100);
  }

  @computed('bookings.[]')
  get uniqueStudentsBooked() {
    // bookings is an async hasMany: the PromiseManyArray wrapper itself
    // isn't iterable, only its resolved `.content` (the actual ManyArray) is.
    return uniqBy(resolved(this.bookings), 'student_id').length;
  }

  @computed('teacherGroupMappings.[]')
  get uniqueStudentsInGroups() {
    return uniq(
      this.teacherGroupMappings.reduce((learnerIds: unknown[], mapping) => {
        return mapping.groups.reduce((ids, group) => {
          // `push` returns a length, so return `ids` explicitly.
          // TS-BUG: only the linced-app group model has currentLearnerMembers.
          // TS-STANDIN(group): the linced-app getter.
          ids.push(
            ...mapBy(
              (group as unknown as { currentLearnerMembers: unknown[] })
                .currentLearnerMembers,
              'personId',
            ),
          );
          return ids;
        }, learnerIds);
      }, []),
    ).length;
  }

  @computed('uniqueStudentsBooked', 'uniqueStudentsInGroups')
  get studentsBookedPercent() {
    if (this.uniqueStudentsInGroups === 0) {
      return 0;
    }
    return Math.round(
      (this.uniqueStudentsBooked / this.uniqueStudentsInGroups) * 100,
    );
  }

  @computed('dates.@each.sessions', 'slotFootprint', 'slotLength')
  get sessionBreakIndexesByDate() {
    return this.dates.reduce(
      (sessionBreakIndexesByDate: Record<string, number[]>, date) => {
        // Allow for zero index array
        let position = -1;
        // Dates have no default. A saved date has one.
        sessionBreakIndexesByDate[date.date as string] = date.sessions.reduce(
          (sessionBreakIndexes: number[], session) => {
            // Session times have no default. A saved session has both.
            const startTime = moment
              .unix(session.startTime as number)
              .add(this.slotLength, 'minutes');
            const endTime = moment.unix(session.endTime as number);
            // We have at least one slot in the session
            position++;
            // Increment by the number of additional slots we can fit in to assign position
            position += Math.floor(
              endTime.diff(startTime, 'minutes') / this.slotFootprint,
            );
            sessionBreakIndexes.push(position);
            return sessionBreakIndexes;
          },
          [],
        );
        return sessionBreakIndexesByDate;
      },
      {},
    );
  }
}
